import { DarkThemeKey, FilePaths } from '../const';

function findLink(_href: string, { head }: Document, prefix = ''): HTMLLinkElement {
	const links = head.getElementsByTagName('link');
	let foundLink: HTMLLinkElement;
	const searchString = prefix + _href;
	for (let i = 0; i < links.length; i++) {
		const { href, outerHTML } = links[i];

		if (href.includes(searchString) || outerHTML.includes(searchString)) {
			foundLink = links[i];
			break;
		}
	}
	return foundLink;
}

export abstract class Theming {
	static appendLinksToHead = (hrefs: Array<string>, doc: Document, prefix = ''): void => {
		const { head } = doc;
		hrefs.forEach((href) => {
			const existing = findLink(href, doc, prefix);
			if (existing) {
				existing.disabled = false;
			} else {
				const theme = doc.createElement('link');
				theme.setAttribute('rel', 'stylesheet');
				theme.setAttribute('type', 'text/css');
				theme.setAttribute('href', prefix + href);
				head.appendChild(theme);
			}
		});
	};

	static disableLinksOnHead = (hrefList: Array<string>, doc: Document, prefix = ''): void => {
		hrefList.forEach((href) => {
			let foundLink;
			if ((foundLink = findLink(href, doc, prefix))) {
				foundLink.disabled = true;
			}
		});
	};

	static appendThemeFile = (
		lightThemePaths: Array<string> = [],
		darkThemePaths: Array<string> = [],
		doc: Document = document,
		prefix = ''
	): void => {
		if (localStorage.getItem(DarkThemeKey)) {
			Theming.appendLinksToHead(
				localStorage.getItem(DarkThemeKey) === 'true'
					? [...darkThemePaths.concat(FilePaths.DarkTheme)]
					: [...lightThemePaths.concat(FilePaths.LightTheme)],
				doc,
				prefix
			);
		}
	};

	static getTheme = (): string => {
		const ls: string = localStorage.getItem(DarkThemeKey);
		if (ls) {
			return ls === 'true' ? 'dark' : 'light';
		}

		return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
	};

	static cssVarToHex = (cssVar: string): string => {
		// Get hsl value from CssVar
		const computedStyle: CSSStyleDeclaration = getComputedStyle(document.documentElement),
			lightnessEvaluation: string = computedStyle
				.getPropertyValue(cssVar)
				// eslint-disable-next-line no-useless-escape
				.replace(/[a-z]|\s|\(|\)|\%|\'/g, '')
				.split(',')[2];

		function evalCssVar(cssVar: string, trimPercentage = true): number {
			const value: string = computedStyle.getPropertyValue(cssVar);
			return trimPercentage
				? Number(
						value
							.split('')
							.filter((l) => l !== '%')
							.join('')
					)
				: Number(value);
		}

		const h: number = evalCssVar('--bg-h', false);
		let s: number = evalCssVar('--bg-s');
		let l: number = eval(lightnessEvaluation);

		// Convert hsl to hex
		s /= 100;
		l /= 100;

		const c: number = (1 - Math.abs(2 * l - 1)) * s;
		const x: number = c * (1 - Math.abs(((h / 60) % 2) - 1));
		const m: number = l - c / 2;
		let r = 0;
		let g = 0;
		let b = 0;

		if (0 <= h && h < 60) {
			r = c;
			g = x;
			b = 0;
		} else if (60 <= h && h < 120) {
			r = x;
			g = c;
			b = 0;
		} else if (120 <= h && h < 180) {
			r = 0;
			g = c;
			b = x;
		} else if (180 <= h && h < 240) {
			r = 0;
			g = x;
			b = c;
		} else if (240 <= h && h < 300) {
			r = x;
			g = 0;
			b = c;
		} else if (300 <= h && h < 360) {
			r = c;
			g = 0;
			b = x;
		}

		let bh: string, gh: string, rh: string;
		rh = Math.round((r + m) * 255).toString(16);
		gh = Math.round((g + m) * 255).toString(16);
		bh = Math.round((b + m) * 255).toString(16);

		if (rh.length == 1) {
			rh = '0' + r;
		}

		if (gh.length == 1) {
			gh = '0' + g;
		}

		if (bh.length == 1) {
			bh = '0' + b;
		}

		return '#' + rh.toUpperCase() + gh.toUpperCase() + bh.toUpperCase();
	};

	static getRelativePath = (source: string, target: string): string => {
		const sep = source.indexOf('/') !== -1 ? '/' : '\\';
		const targetArr = target.split(sep);
		const sourceArr = source.split(sep);
		const filename = targetArr.pop();
		const targetPath = targetArr.join(sep);

		let relativePath = '';

		while (targetPath.indexOf(sourceArr.join(sep)) === -1) {
			sourceArr.pop();
			relativePath += '..' + sep;
		}

		const relPathArr = targetArr.slice(sourceArr.length);
		if (relPathArr.length) {
			relativePath += relPathArr.join(sep) + sep;
		}

		return relativePath + filename;
	};

	static toggleThemeFiles = (doc = document, prefix = ''): void => {
		if (localStorage.getItem(DarkThemeKey)) {
			const isDark = localStorage.getItem(DarkThemeKey) === 'true';
			Theming.disableLinksOnHead([isDark ? FilePaths.LightTheme : FilePaths.DarkTheme], doc, prefix);
			Theming.appendLinksToHead(isDark ? [FilePaths.DarkTheme] : [FilePaths.LightTheme], doc, prefix);
		} else {
			Theming.disableLinksOnHead([FilePaths.LightTheme, FilePaths.DarkTheme], doc, prefix);
		}
	};
}
